import {
    BenchmarkPerformanceResponse,
    TwrrPortfolioPerformanceResponse,
    XirrPortfolioPerformanceResponse,
    XirrSchemePerformance,
} from '@/types/client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { commonApi, ordersApi } from '@/services/api';
import { formatDate } from '@/utils/formatters';
import CustomRangePerformance from './CustomRangePerformance';
import PerformanceCell from '@/components/utils/PerformanceCell';
import SchemePerfBadges from '@/components/utils/SchemePerfBadges';
import { benchmarkOrder, domesticBenchmarkSymbols, globalBenchmarkSymbols } from '@/components/utils/benchmarkConstants';
import PerformanceLegend from '@/components/utils/PerformanceLegend';
import { PageFrame, PageHeader, Surface, Button, Pill, Note, Placeholder, TabBar } from '@/components/ui';
import styles from './PortfolioPerformance.module.css';
import shared from '@/components/utils/shared.module.css';

type TimePeriodKey = keyof Pick<XirrSchemePerformance, 'calendar_ytd' | 'financial_ytd' | 'mtd' | 'this_week' | '1d'>;

// A table card is either the realtime (current) snapshot or the previous-close snapshot.
type Variant = 'realtime' | 'prevclose';

type TabKey = 'domestic' | 'global' | 'custom';

const TABS: { key: TabKey; label: string }[] = [
    { key: 'domestic', label: 'Domestic' },
    { key: 'global', label: 'Global' },
    { key: 'custom', label: 'Custom Range' },
];

// Domestic shows both the realtime (current) and previous-close columns; global has no
// realtime data, so it only requests the previous-close set.
const DOMESTIC_KEYS = 'current,last_working';
const GLOBAL_KEYS = 'last_working';
const DOMESTIC_BENCHMARK_KEYS = 'current,last_working';
const GLOBAL_BENCHMARK_KEYS = 'last_working';

// Scheme.region values sent to the backend (1 = DOMESTIC, 2 = GLOBAL).
const DOMESTIC_REGION = 1;
const GLOBAL_REGION = 2;

const getNDaysAgo = (n: number): string => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().split('T')[0];
};

// Domestic caps at T-1; global lags an extra day, so it caps at T-2.
const getYesterday = (): string => getNDaysAgo(1);
const getTwoDaysAgo = (): string => getNDaysAgo(2);

const PortfolioPerformance: React.FC = () => {
    const [xirrDomestic, setXirrDomestic] = useState<XirrPortfolioPerformanceResponse | null>(null);
    const [xirrGlobal, setXirrGlobal] = useState<XirrPortfolioPerformanceResponse | null>(null);
    const [twrrDomestic, setTwrrDomestic] = useState<TwrrPortfolioPerformanceResponse | null>(null);
    const [twrrGlobal, setTwrrGlobal] = useState<TwrrPortfolioPerformanceResponse | null>(null);
    const [domesticBenchmarkData, setDomesticBenchmarkData] = useState<BenchmarkPerformanceResponse | null>(null);
    const [globalBenchmarkData, setGlobalBenchmarkData] = useState<BenchmarkPerformanceResponse | null>(null);
    // Domestic loads on mount (after the last-working-day lookup), so it starts in the
    // loading state rather than rendering empty cards until the fetch begins.
    const [domesticLoading, setDomesticLoading] = useState(true);
    const [globalLoading, setGlobalLoading] = useState(false);
    const [domesticBenchmarkLoading, setDomesticBenchmarkLoading] = useState(true);
    const [globalBenchmarkLoading, setGlobalBenchmarkLoading] = useState(false);
    const [domesticError, setDomesticError] = useState<string | null>(null);
    const [globalError, setGlobalError] = useState<string | null>(null);
    const [domesticBenchmarkError, setDomesticBenchmarkError] = useState<string | null>(null);
    const [globalBenchmarkError, setGlobalBenchmarkError] = useState<string | null>(null);
    // Each region carries its own "until previous close" anchor date.
    const [domesticDate, setDomesticDate] = useState<string>(getYesterday);
    const [globalDate, setGlobalDate] = useState<string>(getTwoDaysAgo);
    const [maxDate, setMaxDate] = useState<string>(getYesterday);
    // The date the currently-shown data was actually loaded with (drives the "Prev.
    // Close" chip) — distinct from the picker date, which can change before a refresh.
    const [domesticLoadedDate, setDomesticLoadedDate] = useState<string>(getYesterday);
    const [globalLoadedDate, setGlobalLoadedDate] = useState<string>(getTwoDaysAgo);
    const [activeTab, setActiveTab] = useState<TabKey>('domestic');
    // Regions load lazily on first visit; this tracks which have already fired so
    // switching tabs back and forth doesn't refetch. Refresh reloads explicitly.
    const loadedRegions = useRef<Set<TabKey>>(new Set());
    const isMountedRef = useRef(true);

    // Loads the domestic XIRR + TWRR tables for a given "until previous close" date.
    const loadDomesticData = useCallback(async (date: string) => {
        try {
            setDomesticLoading(true);
            setDomesticError(null);
            const [xirr, twrr] = await Promise.all([
                ordersApi.getPortfolioPerformanceXIRR({ date, keys: DOMESTIC_KEYS, region: DOMESTIC_REGION }),
                ordersApi.getPortfolioPerformanceTWRR({ date, keys: DOMESTIC_KEYS, region: DOMESTIC_REGION }),
            ]);
            if (!isMountedRef.current) return;
            setXirrDomestic(xirr);
            setTwrrDomestic(twrr);
            setDomesticLoadedDate(date);
        } catch (err: any) {
            if (isMountedRef.current) setDomesticError(err.response?.data?.error || 'Failed to load domestic performance data');
        } finally {
            if (isMountedRef.current) setDomesticLoading(false);
        }
    }, []);

    // Loads the global XIRR + TWRR tables (no realtime column) for a given date.
    const loadGlobalData = useCallback(async (date: string) => {
        try {
            setGlobalLoading(true);
            setGlobalError(null);
            const [xirr, twrr] = await Promise.all([
                ordersApi.getPortfolioPerformanceXIRR({ date, keys: GLOBAL_KEYS, region: GLOBAL_REGION }),
                ordersApi.getPortfolioPerformanceTWRR({ date, keys: GLOBAL_KEYS, region: GLOBAL_REGION }),
            ]);
            if (!isMountedRef.current) return;
            setXirrGlobal(xirr);
            setTwrrGlobal(twrr);
            setGlobalLoadedDate(date);
        } catch (err: any) {
            if (isMountedRef.current) setGlobalError(err.response?.data?.error || 'Failed to load global performance data');
        } finally {
            if (isMountedRef.current) setGlobalLoading(false);
        }
    }, []);

    // Both regions read the same benchmark endpoint (it returns every configured
    // benchmark); only the anchor date, the period keys and the state slice differ.
    // Global has no realtime column, so it requests the previous-close set only.
    const loadBenchmarkPerformanceData = useCallback(async (region: 'domestic' | 'global', date: string) => {
        const isDomestic = region === 'domestic';
        const setData = isDomestic ? setDomesticBenchmarkData : setGlobalBenchmarkData;
        const setLoading = isDomestic ? setDomesticBenchmarkLoading : setGlobalBenchmarkLoading;
        const setError = isDomestic ? setDomesticBenchmarkError : setGlobalBenchmarkError;
        try {
            setLoading(true);
            setError(null);
            const response = await ordersApi.getBenchmarkPerformance({
                date,
                keys: isDomestic ? DOMESTIC_BENCHMARK_KEYS : GLOBAL_BENCHMARK_KEYS,
            });
            if (!isMountedRef.current) return;
            setData(response);
        } catch (err: any) {
            if (isMountedRef.current) setError(err.response?.data?.error || 'Failed to load benchmark performance data');
        } finally {
            if (isMountedRef.current) setLoading(false);
        }
    }, []);

    // Fetches a region's anchor date and its tables the first time that tab is shown.
    // Domestic anchors to the last working day (T-1); global lags an extra day, so it
    // makes its own call with days=2 to get T-2.
    const ensureRegionLoaded = useCallback(async (region: TabKey) => {
        // Custom Range fetches on demand from within its own component.
        if (region === 'custom' || loadedRegions.current.has(region)) return;
        loadedRegions.current.add(region);

        if (region === 'domestic') {
            // Show the loaders while the anchor date is looked up, not just once the
            // table requests start.
            setDomesticLoading(true);
            setDomesticBenchmarkLoading(true);
            let date = getYesterday();
            try {
                const fetched = (await commonApi.getLastWorkingDay()).last_working_day;
                if (!isMountedRef.current) return;
                date = fetched;
                setDomesticDate(fetched);
            } catch (err) {
                console.error('Error fetching domestic last working day:', err);
                if (!isMountedRef.current) return;
            }
            loadDomesticData(date);
            loadBenchmarkPerformanceData('domestic', date);
        } else if (region === 'global') {
            setGlobalLoading(true);
            setGlobalBenchmarkLoading(true);
            let date = getTwoDaysAgo();
            try {
                const fetched = (await commonApi.getLastWorkingDay({ days: 2 })).last_working_day;
                if (!isMountedRef.current) return;
                date = fetched;
                setGlobalDate(fetched);
            } catch (err) {
                console.error('Error fetching global last working day:', err);
                if (!isMountedRef.current) return;
            }
            loadGlobalData(date);
            loadBenchmarkPerformanceData('global', date);
        }
    }, [loadDomesticData, loadGlobalData, loadBenchmarkPerformanceData]);

    useEffect(() => {
        isMountedRef.current = true;
        setMaxDate(getYesterday());
        ensureRegionLoaded('domestic');
        return () => { isMountedRef.current = false; };
    }, [ensureRegionLoaded]);

    const handleTabChange = (region: TabKey) => {
        setActiveTab(region);
        if (region === 'custom') return;
        // First visit fetches the default date and loads; revisiting refreshes the
        // tables against whatever date is currently selected for that region.
        if (loadedRegions.current.has(region)) {
            if (region === 'domestic') handleDomesticRefresh();
            else handleGlobalRefresh();
        } else {
            ensureRegionLoaded(region);
        }
    };

    // Each region's benchmarks refresh together with that region's tables, against
    // that region's own anchor date.
    const handleDomesticRefresh = () => {
        loadDomesticData(domesticDate);
        loadBenchmarkPerformanceData('domestic', domesticDate);
    };

    const handleGlobalRefresh = () => {
        loadGlobalData(globalDate);
        loadBenchmarkPerformanceData('global', globalDate);
    };

    const handleDomesticDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDomesticDate(e.target.value);
    };

    const handleGlobalDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setGlobalDate(e.target.value);
    };

    const timeColumns: { key: TimePeriodKey; label: string }[] = [
        { key: 'calendar_ytd', label: 'Cal. YTD (%)' },
        { key: 'financial_ytd', label: 'Fin. YTD (%)' },
        { key: 'mtd', label: 'MTD (%)' },
        { key: 'this_week', label: 'Week (%)' },
        { key: '1d', label: 'Day (%)' },
    ];

    // A small pill in the top-right of every table card telling the reader whether the
    // numbers are live or anchored to the previous close. For the previous-close set we
    // also surface the anchor date so it's clear which day the figures are as of.
    const renderVariantChip = (variant: Variant, date?: string) =>
        variant === 'realtime'
            ? <Pill tone="positive">Realtime</Pill>
            : <Pill tone="neutral" solid>{`Prev. close${date ? ` · ${formatDate(date)}` : ''}`}</Pill>;

    const renderCardShell = (metricLabel: string, variant: Variant, body: React.ReactNode, date?: string) => (
        <Surface title={metricLabel} meta={renderVariantChip(variant, date)}>
            {body}
        </Surface>
    );

    const renderLoadingBody = (label: string) => (
        <Placeholder kind="loading" message={`Loading ${label} data…`} />
    );

    const renderPortfolioTable = (
        responseData: XirrPortfolioPerformanceResponse | TwrrPortfolioPerformanceResponse | null,
        isLoading: boolean,
        type: 'xirr' | 'twrr',
        metricLabel: string,
        variant: Variant,
        date: string,
    ) => {
        if (isLoading) {
            return renderCardShell(metricLabel, variant, renderLoadingBody(type.toUpperCase()), date);
        }

        if (!responseData) return null;

        const data = variant === 'realtime' ? responseData.current : responseData.last_working;
        if (!data) return null;

        return renderCardShell(metricLabel, variant, (
            <div className={styles['table-scroll']}>
                <table className={`${shared.table} ${styles['portfolio-performance-table']}`}>
                    <thead>
                        <tr>
                            <th className={styles['scheme-code']}>Scheme</th>
                            {timeColumns.map(col => (
                                <th key={col.key} className={styles['period-column']}>{col.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(data).map(([schemeId, schemeData], index) => (
                            <tr key={schemeId} className={index % 2 === 0 ? styles['row-even'] : styles['row-odd']}>
                                <td className={styles['scheme-code']}>
                                    <span className={styles['scheme-cell-inner']}>
                                        <Tooltip
                                            title={schemeData.full_name || ''}
                                            placement="top"
                                            arrow
                                            disableHoverListener={!schemeData.full_name}
                                            disableTouchListener={!schemeData.full_name}
                                            disableFocusListener={!schemeData.full_name}
                                        >
                                            <span
                                                className={styles['scheme-cell-text']}
                                                style={{ cursor: schemeData.full_name ? 'help' : 'default' }}
                                            >
                                                {schemeData.scheme_code}
                                                {schemeData.display_name && ` (${schemeData.display_name})`}
                                            </span>
                                        </Tooltip>

                                        <SchemePerfBadges scheme={schemeData} />
                                    </span>
                                </td>
                                {timeColumns.map(col => {
                                    const displayData = schemeData[col.key];
                                    const percentKey = type === 'xirr' ? 'xirr_percent' : 'twrr_percent';
                                    const value = displayData[percentKey as keyof typeof displayData] as number | null;
                                    return (
                                        <PerformanceCell
                                            key={`${col.key}-${type}`}
                                            value={value}
                                            status={displayData.status}
                                            msg={displayData.msg}
                                        />
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        ), date);
    };

    // `symbols` scopes the table to one region's benchmarks — the endpoint returns
    // every configured benchmark in a single payload.
    const renderBenchmarkTable = (
        responseData: BenchmarkPerformanceResponse | null,
        isLoading: boolean,
        symbols: string[],
        metricLabel: string,
        variant: Variant,
        date: string,
    ) => {
        if (isLoading) {
            return renderCardShell(metricLabel, variant, renderLoadingBody('benchmark'), date);
        }

        if (!responseData) return null;

        const allBenchmarks = variant === 'realtime' ? responseData.current : responseData.last_working;
        if (!allBenchmarks) return null;

        const data = Object.fromEntries(
            Object.entries(allBenchmarks).filter(([, benchmarkInfo]) => symbols.includes(benchmarkInfo.symbol)),
        );
        if (Object.keys(data).length === 0) return null;

        return renderCardShell(metricLabel, variant, (
            <div className={styles['table-scroll']}>
                <table className={`${shared.table} ${styles['portfolio-performance-table']}`}>
                <thead>
                <tr>
                    <th className={styles['scheme-column']}>Name</th>
                    {timeColumns.map(col => (
                        <th key={col.key} className={styles['period-column']}>{col.label}</th>
                    ))}
                </tr>
                </thead>
                <tbody>
                {Object.entries(data)
                    .sort(([, a], [, b]) => {
                        const indexA = benchmarkOrder.indexOf(a.symbol);
                        const indexB = benchmarkOrder.indexOf(b.symbol);
                        return indexA - indexB;
                    })
                    .map(([benchmarkId, benchmarkInfo], index) => (
                        <tr key={benchmarkId} className={index % 2 === 0 ? styles['row-even'] : styles['row-odd']}>
                            <td className={styles['scheme-code']}>{benchmarkInfo.name}</td>
                            {timeColumns.map(col => {
                                const displayData = benchmarkInfo[col.key];
                                return (
                                    <PerformanceCell
                                        key={`${col.key}-change`}
                                        value={displayData.change_percent}
                                        status={displayData.status}
                                        msg={displayData.msg}
                                    />
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>
        ), date);
    };

    // Renders beside the Domestic/Global pill in the page header instead of
    // its own row below it.
    const renderInlineToolbar = (
        date: string,
        onDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
        onRefresh: () => void,
        isLoading: boolean,
        idPrefix: string,
    ) => (
        <div className={styles['region-toolbar']}>
            <div className={shared['filter-group']}>
                <label htmlFor={`${idPrefix}-date-select`}>Until prev close</label>
                <input
                    type="date"
                    id={`${idPrefix}-date-select`}
                    value={date}
                    onChange={onDateChange}
                    max={maxDate}
                    className={shared.input}
                />
            </div>
            <Button variant="primary" onClick={onRefresh} disabled={isLoading}>
                {isLoading ? 'Loading…' : 'Refresh'}
            </Button>
        </div>
    );

    return (
        <PageFrame
            header={
                <PageHeader
                    title="Portfolio Performance"
                    subtitle="XIRR, TWRR and benchmark returns per scheme, live and against the previous close."
                    actions={
                        <>
                            {activeTab === 'domestic' && renderInlineToolbar(
                                domesticDate,
                                handleDomesticDateChange,
                                handleDomesticRefresh,
                                domesticLoading || domesticBenchmarkLoading,
                                'domestic',
                            )}
                            {activeTab === 'global' && renderInlineToolbar(
                                globalDate,
                                handleGlobalDateChange,
                                handleGlobalRefresh,
                                globalLoading || globalBenchmarkLoading,
                                'global',
                            )}
                            {/* align="end" bottom-aligns it with the labelled toolbar beside it. */}
                            <TabBar
                                align="end"
                                ariaLabel="Region"
                                value={activeTab}
                                onChange={key => handleTabChange(key as typeof activeTab)}
                                tabs={TABS.map(tab => ({ value: tab.key, label: tab.label }))}
                            />
                        </>
                    }
                />
            }
        >
            {/* Domestic — each region has its own "until previous close" date */}
            {activeTab === 'domestic' && (
                <>
                    {domesticError && <Note tone="error">Domestic: {domesticError}</Note>}
                    {domesticBenchmarkError && <Note tone="error">Benchmarks: {domesticBenchmarkError}</Note>}

                    <div className={styles['grid-container']}>
                        {renderPortfolioTable(xirrDomestic, domesticLoading, 'xirr', 'XIRR', 'realtime', domesticLoadedDate)}
                        {renderPortfolioTable(xirrDomestic, domesticLoading, 'xirr', 'XIRR', 'prevclose', domesticLoadedDate)}
                    </div>

                    <div className={styles['grid-container']}>
                        {renderPortfolioTable(twrrDomestic, domesticLoading, 'twrr', 'TWRR', 'realtime', domesticLoadedDate)}
                        {renderPortfolioTable(twrrDomestic, domesticLoading, 'twrr', 'TWRR', 'prevclose', domesticLoadedDate)}
                    </div>

                    <div className={styles['grid-container']}>
                        {renderBenchmarkTable(domesticBenchmarkData, domesticBenchmarkLoading, domesticBenchmarkSymbols, 'Benchmarks', 'realtime', domesticLoadedDate)}
                        {renderBenchmarkTable(domesticBenchmarkData, domesticBenchmarkLoading, domesticBenchmarkSymbols, 'Benchmarks', 'prevclose', domesticLoadedDate)}
                    </div>
                </>
            )}

            {/* Global — no realtime column; only the previous-close set, own date */}
            {activeTab === 'global' && (
                <>
                    {globalError && <Note tone="error">Global: {globalError}</Note>}
                    {globalBenchmarkError && <Note tone="error">Benchmarks: {globalBenchmarkError}</Note>}

                    <div className={styles['grid-container']}>
                        {renderPortfolioTable(xirrGlobal, globalLoading, 'xirr', 'XIRR', 'prevclose', globalLoadedDate)}
                        {renderPortfolioTable(twrrGlobal, globalLoading, 'twrr', 'TWRR', 'prevclose', globalLoadedDate)}
                    </div>

                    <div className={styles['grid-container']}>
                        {renderBenchmarkTable(globalBenchmarkData, globalBenchmarkLoading, globalBenchmarkSymbols, 'Benchmarks', 'prevclose', globalLoadedDate)}
                    </div>
                </>
            )}

            {activeTab === 'custom' && <CustomRangePerformance />}

            <PerformanceLegend />
        </PageFrame>
    );
};

export default PortfolioPerformance;